import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useCustomers } from '@/hooks/crm';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SourceDataPoint, Review } from '@/types/index';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmailTemplatePreview from './EmailTemplatePreview';
import { PersonalizationToolbar } from './PersonalizationToolbar';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { Pencil, Clock, CalendarIcon, X, Star, Check } from 'lucide-react';
import { processTemplateForSending } from '@/lib/email/canva-template-generator';
import { format } from 'date-fns';
import { Badge } from "@/components/ui/badge";
import { getTextColorForBackground } from '@/lib/core/utils';
import { validateEmail } from '@/lib/core/validation/input-validation';

interface BulkMailTabProps {
  businessId?: string; // Optional business ID for filtering
}

const BulkMailTab: React.FC<BulkMailTabProps> = () => {
  const { selectedBusinessId } = useBusinesses();

  // State restoration flag
  const [hasRestoredState, setHasRestoredState] = useState(false);

  // Initialize all state with empty/default values - will be restored after businessId loads
  const [subject, setSubject] = useState('');
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedSources, setSelectedSources] = useState<string[]>(['review_forms', 'loyalty_programs']);
  const [selectedFormsOrPrograms, setSelectedFormsOrPrograms] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<number[]>([1, 2, 3, 4, 5]);
  const [sortOption, setSortOption] = useState<string>("newest");

  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  const { customers: allCustomers, loading: customersLoading } = useCustomers({
    businessId: selectedBusinessId || '',
    lifecycleFilter: 'all',
    sourceFilter: 'all',
    sortBy: 'created_at_desc',
  });

  const [selectedSavedTemplateId, setSelectedSavedTemplateId] = useState<string | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<any[]>([]);

  // State for edit mode for sender name and reply-to
  const [isEditingSenderName, setIsEditingSenderName] = useState(false);
  const [isEditingReplyTo, setIsEditingReplyTo] = useState(false);

  // Data for source dropdown
  const [reviewForms, setReviewForms] = useState<{id: string, title: string}[]>([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<{id: string, name: string}[]>([]);

  // Scheduled emails state
  const [scheduledEmails, setScheduledEmails] = useState<any[]>([]);
  const [showScheduledEmails, setShowScheduledEmails] = useState(false);

  useEffect(() => {
    if (hasRestoredState || !selectedBusinessId) return;

    const DRAFT_KEY = `bulkMailDraft_${selectedBusinessId}`;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.subject !== undefined) setSubject(draft.subject);
        if (draft.senderName !== undefined) setSenderName(draft.senderName);
        if (draft.replyTo !== undefined) setReplyTo(draft.replyTo);
        if (draft.selectedSavedTemplateId !== undefined) setSelectedSavedTemplateId(draft.selectedSavedTemplateId);
        if (draft.editedMainMessage !== undefined) setEditedMainMessage(draft.editedMainMessage);
        if (draft.editedHeaderText !== undefined) setEditedHeaderText(draft.editedHeaderText);
        if (draft.startDate) setStartDate(new Date(draft.startDate));
        if (draft.endDate) setEndDate(new Date(draft.endDate));
        if (draft.selectedSources) setSelectedSources(draft.selectedSources);
        if (draft.selectedFormsOrPrograms) setSelectedFormsOrPrograms(draft.selectedFormsOrPrograms);
        if (draft.selectedRatings) setSelectedRatings(draft.selectedRatings);
        if (draft.sortOption) setSortOption(draft.sortOption);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
    setHasRestoredState(true);
  }, [selectedBusinessId, hasRestoredState]);

  useEffect(() => {
    supabase.from('email_templates').select('*').then(({ data }) => setSavedTemplates(data || []));
  }, []);

  const fetchScheduledEmails = async () => {
    if (!selectedBusinessId) return;
    const { data } = await supabase
      .from('scheduled_emails')
      .select('*')
      .eq('business_id', selectedBusinessId)
      .order('scheduled_for', { ascending: true });
    setScheduledEmails(data || []);
  };

  useEffect(() => { fetchScheduledEmails(); }, [selectedBusinessId]);

  useEffect(() => {
    if (!selectedBusinessId || !hasRestoredState) return;
    supabase.from('businesses').select('sender_name, name, reply_to').eq('id', selectedBusinessId).single()
      .then(({ data }) => {
        if (data) {
          if (!senderName) setSenderName(data.sender_name || data.name || '');
          if (!replyTo) setReplyTo(data.reply_to || '');
        }
      });
  }, [selectedBusinessId, hasRestoredState]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    Promise.all([
      supabase.from('review_forms').select('id, title').eq('business_id', selectedBusinessId).eq('active_status', true),
      supabase.from('loyalty_programs').select('id, name').eq('business_id', selectedBusinessId).eq('is_active', true).is('deleted_at', null)
    ]).then(([formsRes, loyaltyRes]) => {
      if (formsRes.data) setReviewForms(formsRes.data);
      if (loyaltyRes.data) setLoyaltyPrograms(loyaltyRes.data);
    });
  }, [selectedBusinessId]);

  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState<any>(null);
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editedMainMessage, setEditedMainMessage] = useState('');
  const [savedMainMessage, setSavedMainMessage] = useState('');
  const [editedHeaderText, setEditedHeaderText] = useState('');
  const [savedHeaderText, setSavedHeaderText] = useState('');
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditingMessage && messageTextareaRef.current) messageTextareaRef.current.focus();
  }, [isEditingMessage]);

  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleHour, setScheduleHour] = useState('09');
  const [scheduleMinute, setScheduleMinute] = useState('00');

  const getCurrentTimeGMTPlus1 = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));

  const isTimeValid = (hour: string, minute: string) => {
    if (!scheduleDate) return true;
    const selectedDateStart = new Date(scheduleDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    if (selectedDateStart.getTime() > todayStart.getTime()) return true;
    if (selectedDateStart.getTime() === todayStart.getTime()) {
      const currentGMT1 = getCurrentTimeGMTPlus1();
      return parseInt(hour) * 60 + parseInt(minute) > currentGMT1.getHours() * 60 + currentGMT1.getMinutes();
    }
    return true;
  };

  useEffect(() => {
    if (!selectedBusinessId || !hasRestoredState) return;

    const DRAFT_KEY = `bulkMailDraft_${selectedBusinessId}`;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        subject, senderName, replyTo, selectedSavedTemplateId, editedMainMessage, editedHeaderText,
        startDate: startDate?.toISOString(), endDate: endDate?.toISOString(),
        selectedSources, selectedFormsOrPrograms, selectedRatings, sortOption,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [
    selectedBusinessId, hasRestoredState, subject, senderName, replyTo,
    selectedSavedTemplateId, editedMainMessage, editedHeaderText, startDate, endDate,
    selectedSources, selectedFormsOrPrograms, selectedRatings, sortOption
  ]);

  const clearDraft = () => {
    if (!selectedBusinessId) return;
    try {
      localStorage.removeItem(`bulkMailDraft_${selectedBusinessId}`);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  const [lastInitializedTemplateId, setLastInitializedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasRestoredState) return;

    if (selectedSavedTemplateId) {
      const templateToLoad = savedTemplates.find(t => t.id === selectedSavedTemplateId);
      if (templateToLoad?.configuration) {
        setSelectedTemplateConfig(templateToLoad.configuration);
        if (lastInitializedTemplateId !== selectedSavedTemplateId) {
          // When switching templates (lastInitializedTemplateId is not null), always use the new template's message
          // When first loading (restoring from draft), use the current value if available
          const isSwitchingTemplates = lastInitializedTemplateId !== null;

          setEditedMainMessage(currentMessage => {
            const newMessage = isSwitchingTemplates
              ? (templateToLoad.configuration.mainMessage || '')
              : (currentMessage || templateToLoad.configuration.mainMessage || '');
            setSavedMainMessage(newMessage);
            return newMessage;
          });
          setEditedHeaderText(currentHeader => {
            const newHeader = isSwitchingTemplates
              ? (templateToLoad.configuration.headerText || '')
              : (currentHeader || templateToLoad.configuration.headerText || '');
            setSavedHeaderText(newHeader);
            return newHeader;
          });
          setIsEditingMessage(false);
          setLastInitializedTemplateId(selectedSavedTemplateId);
        }
      } else {
        setSelectedTemplateConfig(null);
        setMessage("Selected template not found or has no configuration data.");
      }
    } else {
      setSelectedTemplateConfig(null);
      setLastInitializedTemplateId(null);
    }
  }, [selectedSavedTemplateId, savedTemplates, hasRestoredState, lastInitializedTemplateId]);

  const getButtonUrl = (config: any) => {
    const baseUrl = 'https://taginn.app';
    switch (config.buttonType) {
      case 'review': return config.selectedReviewId ? `${baseUrl}/review/${config.selectedReviewId}` : '#';
      case 'coupon': return config.selectedCouponId ? `${baseUrl}/coupon-offer/${config.selectedCouponId}` : '#';
      case 'loyalty': return config.selectedLoyaltyId ? `${baseUrl}/get-pass?template=${config.selectedLoyaltyId}` : '#';
      case 'custom': return config.ctaLink || '#';
      default: return '#';
    }
  };

  const getButtonText = (config: any) => {
    if (config.ctaText?.trim()) return config.ctaText;
    switch (config.buttonType) {
      case 'review': return 'Leave Review';
      case 'coupon': return 'Get Coupon';
      case 'loyalty': return 'Get Loyalty Card';
      case 'custom': return 'Click Here';
      default: return 'Button';
    }
  };

  const shouldShowButton = (config: any) => config.buttonType && config.buttonType !== 'none';

  const isAllSourcesSelected = useMemo(() =>
    selectedSources.includes('all') || (selectedSources.includes('review_forms') && selectedSources.includes('loyalty_programs')),
    [selectedSources]
  );

  const isAllFormsOrProgramsSelected = useMemo(() => {
    const allAvailableIds = [
      ...(selectedSources.includes('review_forms') || selectedSources.includes('all') ? reviewForms.map(f => f.id) : []),
      ...(selectedSources.includes('loyalty_programs') || selectedSources.includes('all') ? loyaltyPrograms.map(l => l.id) : [])
    ];
    return selectedFormsOrPrograms.length === allAvailableIds.length && allAvailableIds.length > 0;
  }, [selectedFormsOrPrograms, selectedSources, reviewForms, loyaltyPrograms]);

  const isAllRatingsSelected = useMemo(() => selectedRatings.length === 5, [selectedRatings]);

  const toggleSource = (source: string) => {
    if (source === 'all') {
      setSelectedSources(isAllSourcesSelected ? [] : ['review_forms', 'loyalty_programs']);
    } else {
      setSelectedSources(prev => {
        const filtered = prev.filter(s => s !== 'all');
        return filtered.includes(source) ? filtered.filter(s => s !== source) : [...filtered, source];
      });
    }
  };

  const toggleFormOrProgram = (id: string) => {
    if (id === 'all') {
      const allAvailableIds = [
        ...(selectedSources.includes('review_forms') || selectedSources.includes('all') ? reviewForms.map(f => f.id) : []),
        ...(selectedSources.includes('loyalty_programs') || selectedSources.includes('all') ? loyaltyPrograms.map(l => l.id) : [])
      ];
      setSelectedFormsOrPrograms(isAllFormsOrProgramsSelected ? [] : allAvailableIds);
    } else {
      setSelectedFormsOrPrograms(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    }
  };

  const toggleRating = (rating: number | 'all') => {
    if (rating === 'all') {
      setSelectedRatings(isAllRatingsSelected ? [] : [1, 2, 3, 4, 5]);
    } else {
      setSelectedRatings(prev => prev.includes(rating) ? prev.filter(r => r !== rating) : [...prev, rating].sort());
    }
  };

  useEffect(() => {
    const allAvailableIds = [
      ...(selectedSources.includes('review_forms') ? reviewForms.map(f => f.id) : []),
      ...(selectedSources.includes('loyalty_programs') ? loyaltyPrograms.map(l => l.id) : [])
    ];
    if (allAvailableIds.length > 0) setSelectedFormsOrPrograms(allAvailableIds);
  }, [selectedSources, reviewForms, loyaltyPrograms]);

  const filteredEmailList = useMemo(() => {
    let filtered = allCustomers.filter(customer => {
      if (!customer.email || customer.unsubscribed) return false;
      return validateEmail(customer.email).isValid;
    });

    if (startDate) {
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      filtered = filtered.filter(c => c.last_engagement_at && new Date(c.last_engagement_at) >= startOfDay);
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(c => c.last_engagement_at && new Date(c.last_engagement_at) <= endOfDay);
    }

    if (!selectedSources.includes('all') && !isAllSourcesSelected) {
      filtered = filtered.filter(c =>
        (selectedSources.includes('review_forms') && c.sources.includes('review')) ||
        (selectedSources.includes('loyalty_programs') && c.sources.includes('loyalty'))
      );
    }

    if (selectedSources.includes('review_forms') || selectedSources.includes('all')) {
      filtered = filtered.filter(c => {
        if (c.averageRating !== null) return selectedRatings.includes(Math.round(c.averageRating));
        return !c.sources.includes('review');
      });
    }

    return filtered.map(customer => {
      const validation = validateEmail(customer.email);
      let recentlyEmailed = false;
      let daysSinceLastEmail: number | null = null;
      if (customer.last_emailed_at) {
        daysSinceLastEmail = Math.floor((Date.now() - new Date(customer.last_emailed_at).getTime()) / (1000 * 60 * 60 * 24));
        recentlyEmailed = daysSinceLastEmail < 7;
      }

      return {
        id: customer.id,
        email: customer.email,
        name: customer.full_name,
        rating: customer.averageRating ? Math.round(customer.averageRating) : null,
        tagName: customer.lead_source || null,
        source: customer.sources.join(', ') || null,
        date: customer.last_engagement_at || customer.created_at,
        emailWarning: validation.warnings?.[0] || null,
        lastEmailedAt: customer.last_emailed_at,
        emailCount: customer.email_count,
        recentlyEmailed,
        daysSinceLastEmail,
      };
    });
  }, [allCustomers, startDate, endDate, selectedSources, selectedFormsOrPrograms, selectedRatings, isAllSourcesSelected]);

  useEffect(() => { setSelectedEmails(new Set()); }, [filteredEmailList]);

  const handleCheckboxChange = (email: string, checked: boolean | 'indeterminate') => {
    setSelectedEmails(prev => { const newSet = new Set(prev); if (checked === true) newSet.add(email); else newSet.delete(email); return newSet; });
  };
  const handleSelectAllChange = (checked: boolean | 'indeterminate') => {
    if (checked === true) setSelectedEmails(new Set(filteredEmailList.map(item => item.email)));
    else setSelectedEmails(new Set());
  };

  const isAllSelected = filteredEmailList.length > 0 && selectedEmails.size === filteredEmailList.length;
  const isIndeterminate = selectedEmails.size > 0 && selectedEmails.size < filteredEmailList.length;

  const handleSaveMessage = () => {
    setSavedMainMessage(editedMainMessage); // Save current state
    setSavedHeaderText(editedHeaderText); // Save header text state
    setIsEditingMessage(false);
  };

  const handleCancelEdit = () => {
    // Revert to last saved state, or template default if never saved
    setEditedMainMessage(savedMainMessage || selectedTemplateConfig?.mainMessage || '');
    setEditedHeaderText(savedHeaderText || selectedTemplateConfig?.headerText || '');
    setIsEditingMessage(false);
  };

  const previewConfig = useMemo(() => selectedTemplateConfig
    ? { ...selectedTemplateConfig, mainMessage: editedMainMessage, headerText: editedHeaderText }
    : null, [selectedTemplateConfig, editedMainMessage, editedHeaderText]);

  const handleScheduleEmail = async () => {
    if (!scheduleDate || !scheduleHour || !scheduleMinute) {
      setMessage('Please select a date and time for scheduling.');
      return;
    }
    if (!isTimeValid(scheduleHour, scheduleMinute)) {
      setMessage('Scheduled time must be in the future (GMT+1 timezone).');
      return;
    }

    const scheduledDateTime = new Date(scheduleDate);
    scheduledDateTime.setHours(parseInt(scheduleHour), parseInt(scheduleMinute), 0, 0);

    const nowInGMTPlus1 = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    const scheduledInGMTPlus1 = new Date(scheduledDateTime.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));

    if (scheduledInGMTPlus1 <= nowInGMTPlus1) {
      setMessage('Scheduled time must be in the future (GMT+1 timezone).');
      return;
    }
    await handleSendEmail(scheduledDateTime);
  };

  const handleSendEmail = async (scheduledFor?: Date) => {
    if (selectedEmails.size === 0) {
      setMessage('Please select at least one recipient.');
      return;
    }
    if (!subject) {
      setMessage('Please provide a subject for the email.');
      return;
    }
    if (!selectedTemplateConfig?.html) {
      setMessage('Please select an email template.');
      return;
    }

    setLoading(true); setMessage('');
    const recipientsArray = Array.from(selectedEmails);

    const currentYear = new Date().getFullYear().toString();
    let finalHtmlContent = selectedTemplateConfig.html;

    // Check if this is a Canva-imported template
    if (selectedTemplateConfig.importSource === 'canva') {
      // Process Canva template placeholders
      const processedMainMessage = editedMainMessage.replace(/\n/g, '<br>');
      const processedHeaderText = editedHeaderText.replace(/\n/g, '<br>');
      finalHtmlContent = processTemplateForSending(finalHtmlContent, {
        mainMessage: processedMainMessage,
        originalMainMessage: selectedTemplateConfig.mainMessage,
        headerText: processedHeaderText,
        originalHeaderText: selectedTemplateConfig.headerText,
        subject,
        currentYear,
        // Unsubscribe URL will be handled by bulk-mail function
      });
    } else {
      // Standard template processing
      finalHtmlContent = finalHtmlContent.replace(/{{SUBJECT}}/g, subject);
      finalHtmlContent = finalHtmlContent.replace(/{{CURRENT_YEAR}}/g, currentYear);

      // Replace main message if it was edited
      if (selectedTemplateConfig.mainMessage) {
        const escapedOriginalMessage = selectedTemplateConfig.mainMessage.replace(/\n/g, '<br>');
        const escapedNewMessage = editedMainMessage.replace(/\n/g, '<br>');
        finalHtmlContent = finalHtmlContent.replace(escapedOriginalMessage, escapedNewMessage);
      }
    }

    // Fix button inline styles for email clients - ensure color is inline
    // This fixes old templates that don't have inline styles on buttons
    const buttonColorMatch = finalHtmlContent.match(/\.cta-button\s*\{[^}]*background:\s*([^;]+);/);
    if (buttonColorMatch) {
      const bgColor = buttonColorMatch[1].trim();
      const textColor = getTextColorForBackground(bgColor);

      // Add/update inline styles to button - works for both old and new templates
      // First, remove any existing style attributes on cta-button to avoid duplication
      finalHtmlContent = finalHtmlContent.replace(
        /(<a[^>]*class="[^"]*cta-button[^"]*"[^>]*)\s+style="[^"]*"([^>]*>)/g,
        '$1$2'
      );

      // Then add the correct inline styles
      finalHtmlContent = finalHtmlContent.replace(
        /(<a[^>]*class="[^"]*cta-button[^"]*"[^>]*)(>)/g,
        `$1 style="display: inline-block; background: ${bgColor}; color: ${textColor}; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin-bottom: 24px; font-weight: 600;"$2`
      );
    }

    // Create or find contacts for each email to enable unsubscribe functionality
    const contactsArray = await Promise.all(
      recipientsArray.map(async (email) => {
        try {
          // Check if contact already exists
          const { data: existingContact } = await supabase
            .from('contacts')
            .select('id')
            .eq('business_id', selectedBusinessId)
            .eq('email', email)
            .single();

          if (existingContact) {
            return { email, contactId: existingContact.id };
          }

          // Create new contact if doesn't exist
          const { data: newContact, error: createError } = await supabase
            .from('contacts')
            .insert({
              business_id: selectedBusinessId,
              email,
              lead_source: 'manual',
              lifecycle_stage: 'customer'
            })
            .select('id')
            .single();

          if (createError || !newContact) {
            console.warn(`Could not create contact for ${email}, unsubscribe link will not be available`);
            return { email, contactId: null };
          }

          return { email, contactId: newContact.id };
        } catch (err) {
          console.error(`Error processing contact for ${email}:`, err);
          return { email, contactId: null };
        }
      })
    );

    try {
      const { data, error } = await supabase.functions.invoke('bulk-mail', {
        body: {
          contacts: contactsArray,  // New format with contactIds
          subject,
          htmlBody: finalHtmlContent,
          senderName: senderName || undefined,
          replyTo: replyTo || undefined,
          businessId: selectedBusinessId || undefined,
          scheduledFor: scheduledFor?.toISOString(),
          footerText: selectedTemplateConfig.footerText || undefined,
          unsubscribeText: selectedTemplateConfig.unsubscribeText || undefined
        },
      });
      if (error) {
        setMessage(`Error ${scheduledFor ? 'scheduling' : 'sending'} emails: ${error instanceof Error ? error.message : String(error)}`);
        console.error('Error invoking bulk-mail function:', error);
      }
      else {
        if (scheduledFor) {
          const scheduledTimeGMT1 = scheduledFor.toLocaleString('en-GB', {
            timeZone: 'Europe/Paris',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
          setMessage(`Email scheduled successfully for ${scheduledTimeGMT1} (GMT+1) to ${recipientsArray.length} recipients!`);
          setIsScheduleDialogOpen(false);
          // Refresh scheduled emails list
          fetchScheduledEmails();
        } else {
          // Handle detailed response from bulk-mail function
          const successCount = data?.successCount ?? recipientsArray.length;
          const failedCount = data?.failedCount ?? 0;
          const failedEmails = data?.failedEmails ?? [];

          if (failedCount === 0) {
            setMessage(`✓ All ${successCount} emails sent successfully!`);
          } else if (successCount === 0) {
            const failedList = failedEmails.slice(0, 3).map((f: { email: string }) => f.email).join(', ');
            const moreCount = failedEmails.length > 3 ? ` and ${failedEmails.length - 3} more` : '';
            setMessage(`✗ All ${failedCount} emails failed to send. Failed: ${failedList}${moreCount}`);
          } else {
            const failedList = failedEmails.slice(0, 3).map((f: { email: string }) => f.email).join(', ');
            const moreCount = failedEmails.length > 3 ? ` +${failedEmails.length - 3} more` : '';
            setMessage(`✓ ${successCount} sent, ✗ ${failedCount} failed (${failedList}${moreCount})`);
          }
        }
        console.log('Email sending response:', data);

        // Clear draft after successful send/schedule
        clearDraft();
      }
    } catch (error: unknown) {
      setMessage(`Error ${scheduledFor ? 'scheduling' : 'sending'} emails: ${error instanceof Error ? error.message : String(error)}`);
      console.error('Error sending emails:', error);
    }
    finally { setLoading(false); }
  };

  const handleCancelScheduledEmail = async (scheduledEmailId: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_emails')
        .update({ status: 'cancelled' })
        .eq('id', scheduledEmailId);

      if (error) {
        console.error('Error cancelling scheduled email:', error);
        setMessage('Failed to cancel scheduled email');
      } else {
        setMessage('Scheduled email cancelled successfully');
        fetchScheduledEmails();
      }
    } catch (error) {
      console.error('Error cancelling scheduled email:', error);
      setMessage('Failed to cancel scheduled email');
    }
  };

  const handleDeleteScheduledEmail = async (scheduledEmailId: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_emails')
        .delete()
        .eq('id', scheduledEmailId);

      if (error) {
        console.error('Error deleting scheduled email:', error);
        setMessage('Failed to delete scheduled email');
      } else {
        setMessage('Scheduled email deleted successfully');
        fetchScheduledEmails();
      }
    } catch (error) {
      console.error('Error deleting scheduled email:', error);
      setMessage('Failed to delete scheduled email');
    }
  };

  // Auto-adjust time when date changes and current time becomes invalid
  useEffect(() => {
    if (!scheduleDate) return;

    const selectedDateStart = new Date(scheduleDate);
    selectedDateStart.setHours(0, 0, 0, 0);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    // Only auto-adjust if today is selected
    if (selectedDateStart.getTime() === todayStart.getTime()) {
      const currentGMT1 = getCurrentTimeGMTPlus1();
      const currentHour = currentGMT1.getHours();
      const currentMinute = currentGMT1.getMinutes();

      const selectedHourNum = parseInt(scheduleHour);
      const selectedMinuteNum = parseInt(scheduleMinute);

      // If selected time is in the past, auto-adjust to current time + 2 minutes
      if (selectedHourNum < currentHour ||
          (selectedHourNum === currentHour && selectedMinuteNum <= currentMinute)) {

        const futureMinute = currentMinute + 2;
        const futureHour = currentHour + Math.floor(futureMinute / 60);
        const adjustedMinute = futureMinute % 60;
        const adjustedHour = futureHour % 24;

        setScheduleHour(String(adjustedHour).padStart(2, '0'));
        setScheduleMinute(String(adjustedMinute).padStart(2, '0'));
      }
    }
  }, [scheduleDate, scheduleHour, scheduleMinute]);

  return (
    <div className="p-4 space-y-6">
      <h2 className="text-2xl font-bold dark:text-white">Bulk Mail</h2>

      {/* Split Layout: Left - Fields/Editor, Right - Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT HALF - Form Fields or Message Editor */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-md font-medium">{isEditingMessage ? 'Edit Message' : 'Email Settings'}</h4>
          </div>
          {isEditingMessage ? (
            /* Message Editor - header text + main message */
            <div className="space-y-4">
              {selectedTemplateConfig?.headerText !== undefined && (
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Header Text (H1)</Label>
                  <Input
                    value={editedHeaderText}
                    onChange={(e) => setEditedHeaderText(e.target.value)}
                    placeholder="Enter header text..."
                    className="font-semibold"
                  />
                </div>
              )}
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Main Message</Label>
                <PersonalizationToolbar
                  textareaRef={messageTextareaRef}
                  value={editedMainMessage}
                  onInsert={setEditedMainMessage}
                />
                <Textarea
                  ref={messageTextareaRef}
                  value={editedMainMessage}
                  onChange={(e) => setEditedMainMessage(e.target.value)}
                  className={selectedTemplateConfig?.headerText !== undefined ? "min-h-[350px] resize-none" : "min-h-[400px] resize-none"}
                  placeholder="Enter your email message..."
                />
              </div>
            </div>
          ) : (
            /* Form Fields */
            <div className="p-4 border rounded-md bg-card text-card-foreground shadow-sm space-y-4">
                <div>
                <Label htmlFor="template">Select Email Template</Label>
                <Select onValueChange={setSelectedSavedTemplateId} value={selectedSavedTemplateId || ''}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose a saved template" />
                  </SelectTrigger>
                  <SelectContent>
                    {savedTemplates.length > 0 ? (
                      savedTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-templates" disabled>No saved templates</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1"
                  placeholder="Your Email Subject"
                />
              </div>
              <div>
                <Label htmlFor="senderName">Email Sender Name</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="text"
                    id="senderName"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    readOnly={!isEditingSenderName}
                    className={!isEditingSenderName ? "bg-muted text-muted-foreground cursor-default" : ""}
                    placeholder="Your Business Name"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingSenderName(!isEditingSenderName)}
                    className="px-3"
                  >
                    {isEditingSenderName ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Pre-filled from business settings. Click edit to modify.
                </p>
              </div>
              <div>
                <Label htmlFor="replyTo">Reply-To Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="email"
                    id="replyTo"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    readOnly={!isEditingReplyTo}
                    className={!isEditingReplyTo ? "bg-muted text-muted-foreground cursor-default" : ""}
                    placeholder="reply@yourbusiness.com"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingReplyTo(!isEditingReplyTo)}
                    className="px-3"
                  >
                    {isEditingReplyTo ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Pre-filled from business settings. Click edit to modify.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT HALF - Preview */}
        {selectedTemplateConfig && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-md font-medium">Template Preview</h4>
              <div className="flex gap-2">
                {isEditingMessage ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSaveMessage}
                      className="gap-2"
                    >
                      <Check className="h-4 w-4" />
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="gap-2"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Initialize with template message if not already set
                        if (!editedMainMessage && selectedTemplateConfig?.mainMessage) {
                          setEditedMainMessage(selectedTemplateConfig.mainMessage);
                          setSavedMainMessage(selectedTemplateConfig.mainMessage);
                        }
                        if (!editedHeaderText && selectedTemplateConfig?.headerText) {
                          setEditedHeaderText(selectedTemplateConfig.headerText);
                          setSavedHeaderText(selectedTemplateConfig.headerText);
                        }
                        setIsEditingMessage(true);
                      }}
                      className="gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit Message
                    </Button>
                    {(editedMainMessage !== selectedTemplateConfig.mainMessage || editedHeaderText !== selectedTemplateConfig.headerText) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                          >
                            <X className="h-4 w-4" />
                            Reset
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Reset message?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will discard your changes and restore the original message.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => {
                              setEditedMainMessage(selectedTemplateConfig.mainMessage || '');
                              setEditedHeaderText(selectedTemplateConfig.headerText || '');
                            }}>
                              Reset
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-center border rounded-lg overflow-hidden">
              <EmailTemplatePreview
                configuration={previewConfig || selectedTemplateConfig}
                mode="desktop"
                isAdvancedMode={!!selectedTemplateConfig.html && (!selectedTemplateConfig.preset || selectedTemplateConfig.importSource === 'canva_app')}
                getButtonUrl={getButtonUrl}
                getButtonText={getButtonText}
                shouldShowButton={shouldShowButton}
                isEditingMessage={isEditingMessage}
                editedMainMessage={editedMainMessage}
                editedHeaderText={editedHeaderText}
                onMessageChange={setEditedMainMessage}
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium dark:text-white">Filter Recipients</h3>
          <Button
            variant="outline"
            size="sm"
            className="dark:text-white"
            onClick={() => {
              setStartDate(undefined);
              setEndDate(undefined);
              setSelectedSources(['review_forms', 'loyalty_programs']);
              // Reset forms/programs to all selected
              const allAvailableIds = [
                ...reviewForms.map(f => f.id),
                ...loyaltyPrograms.map(l => l.id)
              ];
              setSelectedFormsOrPrograms(allAvailableIds);
              setSelectedRatings([1, 2, 3, 4, 5]);
              setSortOption("newest");
            }}
          >
            Clear Filters
          </Button>
        </div>
        <div className="grid grid-cols-5 gap-3">
          <div>
            <Label className="mb-1 block dark:text-white text-xs">Order</Label>
            <Select onValueChange={setSortOption} value={sortOption}>
              <SelectTrigger className={`h-9 ${sortOption !== "newest" ? "border-2 border-orange-500" : ""}`}>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="most_stars">Most Stars</SelectItem>
                <SelectItem value="least_stars">Least Stars</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block dark:text-white text-xs">Date Range</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`h-9 w-full justify-start text-left font-normal text-xs ${
                    (!startDate && !endDate) ? "text-muted-foreground" : ""
                  } ${(startDate || endDate) ? "border-2 border-orange-500" : ""}`}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />
                  {startDate ? (
                    endDate ? (
                      `${format(startDate, "MMM dd")} - ${format(endDate, "MMM dd")}`
                    ) : (
                      format(startDate, "MMM dd, y")
                    )
                  ) : (
                    <span>Pick range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: startDate, to: endDate }}
                  onSelect={(range) => {
                    setStartDate(range?.from);
                    setEndDate(range?.to);
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="mb-1 block dark:text-white text-xs">Source Type</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`h-9 w-full justify-start text-xs ${!selectedSources.includes('all') ? "border-2 border-orange-500" : ""}`}
                >
                  {selectedSources.includes('all')
                    ? 'All Sources'
                    : `${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2">
                <div className="space-y-1">
                  <div
                    className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                    onClick={() => toggleSource('all')}
                  >
                    <Checkbox checked={isAllSourcesSelected} />
                    <label className="text-sm cursor-pointer flex-1 font-medium">All Sources</label>
                  </div>
                  <div
                    className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                    onClick={() => toggleSource('review_forms')}
                  >
                    <Checkbox checked={selectedSources.includes('review_forms')} />
                    <label className="text-sm cursor-pointer flex-1">Review Forms</label>
                  </div>
                  <div
                    className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                    onClick={() => toggleSource('loyalty_programs')}
                  >
                    <Checkbox checked={selectedSources.includes('loyalty_programs')} />
                    <label className="text-sm cursor-pointer flex-1">Loyalty Programs</label>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {(selectedSources.includes('review_forms') || selectedSources.includes('loyalty_programs')) && (
            <div>
              <Label className="mb-1 block dark:text-white text-xs">Forms/Programs</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 w-full justify-start text-xs ${selectedFormsOrPrograms.length > 0 ? "border-2 border-orange-500" : ""}`}
                  >
                    {selectedFormsOrPrograms.length === 0
                      ? 'Select'
                      : `${selectedFormsOrPrograms.length} selected`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-2 max-h-[300px] overflow-y-auto">
                  <div className="space-y-1">
                    <div
                      className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                      onClick={() => toggleFormOrProgram('all')}
                    >
                      <Checkbox checked={isAllFormsOrProgramsSelected} />
                      <label className="text-sm cursor-pointer flex-1 font-medium">All</label>
                    </div>
                    {selectedSources.includes('review_forms') && reviewForms.map((form) => (
                      <div
                        key={form.id}
                        className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                        onClick={() => toggleFormOrProgram(form.id)}
                      >
                        <Checkbox checked={selectedFormsOrPrograms.includes(form.id)} />
                        <label className="text-sm cursor-pointer flex-1">{form.title}</label>
                      </div>
                    ))}
                    {selectedSources.includes('loyalty_programs') && loyaltyPrograms.map((program) => (
                      <div
                        key={program.id}
                        className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                        onClick={() => toggleFormOrProgram(program.id)}
                      >
                        <Checkbox checked={selectedFormsOrPrograms.includes(program.id)} />
                        <label className="text-sm cursor-pointer flex-1">{program.name}</label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {(selectedSources.includes('review_forms') || selectedSources.includes('all')) && (
            <div>
              <Label className="mb-1 block dark:text-white text-xs">Rating</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 w-full justify-start text-xs ${selectedRatings.length < 5 ? "border-2 border-orange-500" : ""}`}
                  >
                    {selectedRatings.length === 5
                      ? 'All Ratings'
                      : `${selectedRatings.length} rating${selectedRatings.length > 1 ? 's' : ''}`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-2">
                  <div className="space-y-1">
                    <div
                      className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                      onClick={() => toggleRating('all')}
                    >
                      <Checkbox checked={isAllRatingsSelected} />
                      <label className="text-sm cursor-pointer flex-1 font-medium">All</label>
                    </div>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <div
                        key={rating}
                        className="flex items-center space-x-2 p-1.5 hover:border hover:border-border rounded cursor-pointer transition-colors"
                        onClick={() => toggleRating(rating)}
                      >
                        <Checkbox checked={selectedRatings.includes(rating)} />
                        <label className="text-sm cursor-pointer flex-1 flex items-center gap-1">
                          {rating} <Star className="h-3 w-3 fill-current" />
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

        </div>
      </div>

      <div className="space-y-2">
         <h3 className="text-lg font-medium dark:text-white">Recipients ({filteredEmailList.length} found, {selectedEmails.size} selected)</h3>
         {/* Warning for recently emailed recipients */}
         {(() => {
           const selectedRecentlyEmailed = filteredEmailList.filter(
             item => selectedEmails.has(item.email) && item.recentlyEmailed
           );
           if (selectedRecentlyEmailed.length > 0) {
             return (
               <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-amber-800 dark:text-amber-200 text-sm">
                 <span>📬</span>
                 <span>
                   <strong>{selectedRecentlyEmailed.length}</strong> selected recipient{selectedRecentlyEmailed.length !== 1 ? 's were' : ' was'} emailed in the last 7 days.
                   Consider whether sending again is appropriate.
                 </span>
               </div>
             );
           }
           return null;
         })()}
         {filteredEmailList.length > 0 ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[40px] py-2"><Checkbox id="selectAll" checked={isAllSelected} onCheckedChange={handleSelectAllChange} aria-label="Select all emails" data-state={isIndeterminate ? 'indeterminate' : (isAllSelected ? 'checked' : 'unchecked')} /></TableHead>
                    <TableHead className="dark:text-white py-2">Name</TableHead>
                    <TableHead className="dark:text-white py-2">Email</TableHead>
                    <TableHead className="dark:text-white py-2">Stars</TableHead>
                    <TableHead className="dark:text-white py-2">Source</TableHead>
                    <TableHead className="dark:text-white py-2">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmailList.map(({ id, email, name, rating, tagName, source, date, emailWarning, recentlyEmailed, daysSinceLastEmail, emailCount }) => {
                    // Determine the display text based on source
                    let displaySource = 'N/A';
                    if (tagName) {
                      if (source === 'loyalty_program') {
                        displaySource = `Loyalty Program: ${tagName}`;
                      } else if (source?.includes('Custom Form') || source === 'review_form') {
                        displaySource = `Review Form: ${tagName}`;
                      } else {
                        displaySource = tagName;
                      }
                    } else {
                      displaySource = source || 'N/A';
                    }

                    return (
                      <TableRow key={id} className={`hover:bg-muted/50 ${recentlyEmailed ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                        <TableCell className="py-2"><Checkbox id={`email-${id}`} checked={selectedEmails.has(email)} onCheckedChange={(checked) => handleCheckboxChange(email, checked)} aria-labelledby={`label-email-${id}`} /></TableCell>
                        <TableCell className="dark:text-white py-2">{name || 'N/A'}</TableCell>
                        <TableCell className="dark:text-white py-2">
                          <div className="flex items-center gap-1">
                            {email}
                            {emailWarning && (
                              <span className="text-amber-500 text-xs" title={emailWarning}>
                                ⚠️
                              </span>
                            )}
                            {recentlyEmailed && (
                              <span
                                className="text-amber-600 text-xs cursor-help"
                                title={`Last emailed ${daysSinceLastEmail} day${daysSinceLastEmail !== 1 ? 's' : ''} ago (total: ${emailCount} emails sent)`}
                              >
                                📬
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="dark:text-white py-2">{rating !== null ? `${rating}` : 'N/A'}</TableCell>
                        <TableCell className="dark:text-white py-2">{displaySource}</TableCell>
                        <TableCell className="dark:text-white py-2">{date ? new Date(date).toLocaleDateString() : 'N/A'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
         ) : (<p className="text-sm text-gray-500 dark:text-gray-400">No recipients match the current filters.</p>)}
      </div>

      <div className="space-y-3">
        <div className="flex gap-3">
          <Button
            onClick={() => handleSendEmail()}
            disabled={loading || selectedEmails.size === 0 || !selectedTemplateConfig}
          >
            {loading ? 'Sending...' : `Send Email to ${selectedEmails.size} Selected Recipients`}
          </Button>

          <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                disabled={loading || selectedEmails.size === 0 || !selectedTemplateConfig}
                className="gap-2"
              >
                <Clock className="h-4 w-4" />
                Schedule Email
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Schedule Email</DialogTitle>
                <DialogDescription>
                  Choose when to send this email to {selectedEmails.size} recipients
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {scheduleDate ? format(scheduleDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={scheduleDate}
                        onSelect={setScheduleDate}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Time (GMT+1, 24-hour format)</Label>
                  <div className="flex gap-2">
                    <Select value={scheduleHour} onValueChange={setScheduleHour}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue placeholder="Hour" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = String(i).padStart(2, '0');
                          const currentGMT1 = getCurrentTimeGMTPlus1();
                          const isToday = scheduleDate && new Date(scheduleDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0);
                          const isPastHour = isToday && i < currentGMT1.getHours();

                          return (
                            <SelectItem key={hour} value={hour} disabled={isPastHour}>
                              {hour}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <span className="flex items-center text-xl">:</span>
                    <Select value={scheduleMinute} onValueChange={setScheduleMinute}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue placeholder="Min" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 60 }, (_, i) => {
                          const minute = String(i).padStart(2, '0');
                          const currentGMT1 = getCurrentTimeGMTPlus1();
                          const isToday = scheduleDate && new Date(scheduleDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0);
                          const isCurrentHour = isToday && parseInt(scheduleHour) === currentGMT1.getHours();
                          const isPastMinute = isCurrentHour && i <= currentGMT1.getMinutes();

                          return (
                            <SelectItem key={minute} value={minute} disabled={isPastMinute}>
                              {minute}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-gray-500">
                    Current time (GMT+1): {getCurrentTimeGMTPlus1().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleScheduleEmail} disabled={loading || !scheduleDate}>
                  {loading ? 'Scheduling...' : 'Schedule Email'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {message && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</p>}
      </div>

      {/* Scheduled Emails Section */}
      <div className="space-y-4 border-t pt-6 mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium dark:text-white">Scheduled Emails</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowScheduledEmails(!showScheduledEmails)}
          >
            {showScheduledEmails ? 'Hide' : 'Show'} ({scheduledEmails.filter(e => e.status === 'pending').length})
          </Button>
        </div>

        {showScheduledEmails && (
          <div className="space-y-2">
            {scheduledEmails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="dark:text-white">Subject</TableHead>
                    <TableHead className="dark:text-white">Recipients</TableHead>
                    <TableHead className="dark:text-white">Scheduled For</TableHead>
                    <TableHead className="dark:text-white">Status</TableHead>
                    <TableHead className="dark:text-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduledEmails.map((scheduledEmail) => (
                    <TableRow key={scheduledEmail.id}>
                      <TableCell className="dark:text-white">{scheduledEmail.subject}</TableCell>
                      <TableCell className="dark:text-white">{scheduledEmail.recipient_count}</TableCell>
                      <TableCell className="dark:text-white">
                        {new Date(scheduledEmail.scheduled_for).toLocaleString('en-GB', {
                          timeZone: 'Europe/Paris',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })} (GMT+1)
                      </TableCell>
                      <TableCell className="dark:text-white">
                        <Badge
                          variant={
                            scheduledEmail.status === 'sent' ? 'default' :
                            scheduledEmail.status === 'pending' ? 'secondary' :
                            scheduledEmail.status === 'failed' ? 'destructive' :
                            'outline'
                          }
                        >
                          {scheduledEmail.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {scheduledEmail.status === 'pending' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelScheduledEmail(scheduledEmail.id)}
                            >
                              Cancel
                            </Button>
                          )}
                          {(scheduledEmail.status === 'sent' || scheduledEmail.status === 'failed' || scheduledEmail.status === 'cancelled') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteScheduledEmail(scheduledEmail.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No scheduled emails</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkMailTab;
